import { useState } from 'react'
import { Lock, Plus, Pencil, Trash2, Search, Users, ShieldCheck, UserCircle, Eye } from 'lucide-react'
import { Modal, ConfirmModal } from '../common/Modal'

const PERMISSIONS = ['View Dashboard', 'Manage Users', 'Manage Sub-Admins', 'Manage Items', 'View Transactions', 'Manage Invoices', 'View Reports', 'View Analytics', 'Manage Subscriptions', 'Send Notifications', 'Manage Email', 'View System Logs', 'Manage FAQs', 'Manage Feedback', 'Manage Settings', 'Manage API Keys', 'Backup & Restore']

const ROLES_DATA = [
  {
    id: 'ROL-001', name: 'Super Admin', description: 'Full access to all features and settings',
    color: '#A855F7', users: 1, icon: 'shield',
    permissions: PERMISSIONS,
  },
  {
    id: 'ROL-002', name: 'Admin', description: 'Manage users, content and most system features',
    color: '#4F46E5', users: 3, icon: 'admin',
    permissions: PERMISSIONS.filter(p => !['Manage API Keys', 'Backup & Restore'].includes(p)),
  },
  {
    id: 'ROL-003', name: 'Manager', description: 'Manage daily operations and view reports',
    color: '#06B6D4', users: 5, icon: 'user',
    permissions: ['View Dashboard', 'Manage Users', 'Manage Items', 'View Transactions', 'Manage Invoices', 'View Reports', 'Manage FAQs', 'Manage Feedback'],
  },
  {
    id: 'ROL-004', name: 'Support Agent', description: 'Handle support tickets and user queries',
    color: '#10B981', users: 8, icon: 'user',
    permissions: ['View Dashboard', 'Manage Users', 'View Transactions', 'Manage FAQs', 'Manage Feedback'],
  },
  {
    id: 'ROL-005', name: 'Viewer', description: 'Read-only access to dashboard and reports',
    color: '#F59E0B', users: 12, icon: 'eye',
    permissions: ['View Dashboard', 'View Transactions', 'View Reports', 'View Analytics'],
  },
]

const ICON_MAP = {
  shield: ShieldCheck,
  admin: UserCircle,
  user: Users,
  eye: Eye,
}

export default function RolesPermissions() {
  const [roles, setRoles] = useState(ROLES_DATA)
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState({ name: '', description: '', color: '#4F46E5', permissions: [] })

  const filtered = roles.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.description.toLowerCase().includes(search.toLowerCase())
  )

  const openAdd = () => {
    setActive(null)
    setForm({ name: '', description: '', color: '#4F46E5', permissions: [] })
    setModalOpen(true)
  }

  const openEdit = r => { setActive(r); setForm({ name: r.name, description: r.description, color: r.color, permissions: [...r.permissions] }); setModalOpen(true) }
  const openView = r => { setActive(r); setViewOpen(true) }

  const togglePerm = p => setForm(f => ({
    ...f,
    permissions: f.permissions.includes(p) ? f.permissions.filter(x => x !== p) : [...f.permissions, p]
  }))

  const save = () => {
    if (!form.name.trim()) return
    if (active) {
      setRoles(prev => prev.map(r => r.id === active.id ? { ...r, ...form } : r))
    } else {
      const id = `ROL-${String(roles.length + 1).padStart(3, '0')}`
      setRoles(prev => [{ id, users: 0, icon: 'user', ...form }, ...prev])
    }
    setModalOpen(false)
  }

  const confirmDelete = () => { setRoles(prev => prev.filter(r => r.id !== active.id)); setDeleteOpen(false); setActive(null) }

  const totalUsers = roles.reduce((s, r) => s + r.users, 0)

  const stats = [
    { label: 'Total Roles',    value: roles.length, color: '#4F46E5', glow: 'rgba(79,70,229,0.18)',  variant: 'indigo', trend: 'defined',  Icon: Lock },
    { label: 'Total Users',    value: totalUsers,   color: '#06B6D4', glow: 'rgba(6,182,212,0.15)',  variant: 'cyan',   trend: 'assigned', Icon: Users },
    { label: 'Permissions',    value: PERMISSIONS.length, color: '#10B981', glow: 'rgba(16,185,129,0.15)', variant: 'green', trend: 'available', Icon: ShieldCheck },
    { label: 'Custom Roles',   value: roles.filter(r => !['Super Admin', 'Admin'].includes(r.name)).length, color: '#A855F7', glow: 'rgba(168,85,247,0.15)', variant: 'purple', trend: 'created', Icon: UserCircle },
  ]

  return (
    <div className="dash-page page-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
        <h1 className="page-title">Roles & Permissions</h1>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Role</button>
      </div>

      {/* Stats */}
      <div className="stats-grid cols-4">
        {stats.map(s => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{ position: 'absolute', top: 0, right: 0, width: 110, height: 110, borderRadius: '50%', background: s.glow, filter: 'blur(30px)', transform: 'translate(30%,-30%)', pointerEvents: 'none' }} />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.glow, color: s.color, border: `1px solid ${s.glow}` }}>
                <s.Icon size={15} strokeWidth={1.8} />
              </div>
              <span className="stat-card-trend" style={{ color: s.color, background: `${s.color}15`, border: `1px solid ${s.glow}` }}>{s.trend}</span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${s.color}88,transparent)`, borderRadius: '0 0 var(--radius-lg) var(--radius-lg)' }} />
          </div>
        ))}
      </div>

      {/* Roles Table */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
            <input className="input" style={{ paddingLeft: 32 }} placeholder="Search roles…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr><th>Role</th><th>Description</th><th>Permissions</th><th>Users</th><th /></tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const RIcon = ICON_MAP[r.icon] || Users
                return (
                  <tr key={r.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', background: `${r.color}18`, border: `1px solid ${r.color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: r.color, flexShrink: 0 }}>
                          <RIcon size={15} strokeWidth={1.8} />
                        </div>
                        <span style={{ fontWeight: 600, color: 'var(--text1)', fontSize: 'var(--fs-sm)' }}>{r.name}</span>
                      </div>
                    </td>
                    <td style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)', maxWidth: 260 }}>{r.description}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 300 }}>
                          {r.permissions.slice(0, 3).map(p => (
                            <span key={p} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 'var(--radius-full)', background: `${r.color}15`, color: r.color, border: `1px solid ${r.color}28`, fontWeight: 600, whiteSpace: 'nowrap' }}>{p}</span>
                          ))}
                          {r.permissions.length > 3 && (
                            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 'var(--radius-full)', background: 'var(--bg-raised)', color: 'var(--text3)', border: '1px solid var(--border)', fontWeight: 600 }}>+{r.permissions.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text2)' }}>
                        <Users size={12} /> {r.users}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-icon" onClick={() => openView(r)} title="View"><Eye size={14} /></button>
                        <button className="btn btn-ghost btn-icon" onClick={() => openEdit(r)} title="Edit"><Pencil size={14} /></button>
                        <button className="btn btn-ghost btn-icon" onClick={() => { setActive(r); setDeleteOpen(true) }} title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={modalOpen}
        title={active ? 'Edit Role' : 'Add Role'}
        subtitle="Configure role name and permissions"
        onClose={() => setModalOpen(false)}
        variant={active ? 'edit' : 'add'}
        footer={<>
          <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>{active ? 'Update Role' : 'Create Role'}</button>
        </>}
      >
        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Role Name</label>
            <input className="input" placeholder="e.g. Content Editor" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>Color</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ width: 40, height: 36, padding: 2, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-raised)', cursor: 'pointer' }} />
              <input className="input" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ flex: 1 }} />
            </div>
          </div>
          <div className="form-field full">
            <label>Description</label>
            <input className="input" placeholder="Brief description of this role" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="form-field full">
            <label>Permissions</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, padding: '10px 0' }}>
              {PERMISSIONS.map(p => (
                <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 'var(--fs-xs)', color: form.permissions.includes(p) ? 'var(--text1)' : 'var(--text3)', fontWeight: form.permissions.includes(p) ? 600 : 400 }}>
                  <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)}
                    style={{ accentColor: form.color, width: 14, height: 14, cursor: 'pointer' }} />
                  {p}
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        open={viewOpen}
        title={active?.name || 'Role'}
        subtitle={active?.description}
        onClose={() => setViewOpen(false)}
        variant="edit"
        footer={<button className="btn btn-outline" onClick={() => setViewOpen(false)}>Close</button>}
      >
        {active && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'var(--space-md)', background: 'var(--bg-raised)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
              <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', background: `${active.color}18`, border: `1px solid ${active.color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: active.color }}>
                {(() => { const I = ICON_MAP[active.icon] || Users; return <I size={18} strokeWidth={1.8} /> })()}
              </div>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--text1)' }}>{active.name}</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)', marginTop: 2 }}>{active.users} users assigned</div>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Permissions ({active.permissions.length})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {active.permissions.map(p => (
                  <span key={p} style={{ fontSize: 'var(--fs-xs)', padding: '4px 10px', borderRadius: 'var(--radius-full)', background: `${active.color}15`, color: active.color, border: `1px solid ${active.color}28`, fontWeight: 600 }}>{p}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete Role"
        message={`Remove the "${active?.name}" role permanently?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      />

      <style>{`.dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}`}</style>
    </div>
  )
}